import { FRAIS_CAT } from '../constants/accounting';
import { categorieDuTexte } from './lecture-ticket';

/** Le texte en minuscules, sans accents. */
const simple = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/**
 * Le texte en minuscules et sans accents, caractère pour caractère : chaque
 * position y est celle du texte d'origine, pour couper l'un comme l'autre.
 */
const calque = (s) =>
  [...s].map((c) => {
    const b = simple(c);
    return b.length === 1 ? b : c.length === 1 ? c : ' ';
  });

// Ce que la banque ajoute autour du commerçant : le genre d'ordre, la carte,
// le moyen de paiement. Lu sur le texte en minuscules et sans accents.
const BRUIT = new RegExp(
  [
    /achats?(?:\s*\/\s*prestations? de services?)?(?: en ligne| sans contact)?/,
    /prestations? de services?/,
    /paiements?(?: par)?(?: carte)?(?: de (?:debit|credit))?(?: maestro)?/,
    /cartes? de (?:debit|credit)/,
    /(?:no|n°|nr\.?|numero)\s*(?:de )?carte/,
    /carte(?:\s*(?:n°|no\.?|nr\.?))?/,
    /mastercard/,
    /maestro/,
    /visa(?: debit)?/,
    /v pay/,
    /debit ?card/,
    /debitkarte/,
    /postfinance card/,
    /einkauf/,
    /kartenzahlung/,
    /zahlung/,
    /belastung/,
    /lastschrift/,
    /twint(?: achat| paiement)?/,
    /envoi d.argent(?: a)?/,
    /argent recu(?: de)?/,
    /ordres?(?: de paiement| e-banking| permanent| de virement)?/,
    /e-banking/,
    /dauerauftrag/,
    /zahlungsauftrag/,
    /prelevements?(?: lsv\+?| direct| d.especes)?/,
    /lsv\+?/,
    /recouvrements?(?: direct)?/,
    /debit direct/,
    /retraits?(?: d.especes)?/,
    /bancomat/,
    /postomat/,
    /geldautomat/,
    /bargeldbezug/,
    /dmc-tancomat/,
    /tancomat/,
    /virements?(?: de| du compte| recu(?: de)?| en faveur de)?/,
    /versements?(?: de)?/,
    /bonification/,
    /credit groupe(?: qr-facture)?/,
    /credit(?: de)?/,
    /debit/,
    /gutschrift/,
    /payment from/,
    // Les mêmes mots dans les autres langues : anglais, espagnol, portugais,
    // italien, néerlandais, albanais, serbe/croate/bosniaque, polonais, turc,
    // roumain. Une banque du Kosovo, d'Espagne ou d'Angleterre écrit son
    // libellé dans sa langue ; le commerçant reste.
    /(?:contactless )?card (?:payment|purchase)/,
    /pos (?:purchase|payment)/,
    /purchase/,
    /contactless/,
    /compras?(?: con tarjeta| tarjeta| com cartao| cartao)?/,
    /pagos?(?: con tarjeta| tarjeta)?/,
    /tarjeta(?: de (?:debito|credito))?/,
    /pagamentos?(?: com cartao| pos| con carta)?/,
    /cartao(?: de (?:debito|credito))?/,
    /acquisto(?: pos| con carta)?/,
    /pagamento pos/,
    /pinbetaling/,
    /betaling/,
    /aankoop/,
    /pagese(?: me karte)?/,
    /blerje(?: me karte)?/,
    /placanje(?: karticom)?/,
    /kupovina/,
    /platnosc(?: karta)?/,
    /zakup(?: karta)?/,
    /kart(?:la)? (?:ile )?odeme/,
    /harcama/,
    /plata(?: cu cardul)?/,
  ]
    .map((r) => r.source)
    .join('|'),
  'g',
);

// Les petits mots laissés seuls en tête quand le reste est parti.
const EN_TETE = /^(?:(?:du|de|des|d'|a|au|aux|chez|from|von|bei|par|pour|le|la|les)\s+)+/;

/** La communication qui suit le commerçant : « Facture 88412 », « Référence … ». */
const SUITE =
  /\b(?:factures?|rechnung|fattura|invoice|ref(?:erence)?|communication|mitteilung|motif)\b/;

/**
 * Le commerçant et son lieu, tels que le patron les lirait : « Achat
 * Mastercard 31.08.2026 08:21 Migros Plan-les-Ouates » → « Migros
 * Plan-les-Ouates », « Paiement carte de débit No de carte XXXX1234 … Coop
 * Pronto Genève » → « Coop Pronto Genève », « TWINT Envoi d'argent à Jean
 * Dupont +41791234567 » → « Jean Dupont ». La carte, le genre d'ordre, la
 * date, l'heure, le numéro de carte ou de téléphone et l'IBAN s'en vont.
 *
 * @param {string} libelle le libellé du mouvement
 * @returns {string} le commerçant et son lieu ('' si rien ne reste)
 */
export function commerceDuLibelle(libelle) {
  const origine = String(libelle || '')
    .normalize('NFC')
    .replace(/\s+/g, ' ');
  const lettres = [...origine];
  const bas = calque(origine);
  const efface = (debut, fin) => {
    for (let i = debut; i < fin; i++) {
      lettres[i] = ' ';
      bas[i] = ' ';
    }
  };
  const retire = (re) => {
    const texte = bas.join('');
    for (const m of texte.matchAll(re)) {
      // Un mot entier : ni lettre ni chiffre collés avant ou après.
      const avant = texte[m.index - 1];
      const apres = texte[m.index + m[0].length];
      if ((avant && /[a-z0-9]/.test(avant)) || (apres && /[a-z0-9]/.test(apres))) continue;
      efface(m.index, m.index + m[0].length);
    }
  };
  // Les dates (31.08.2026, 31.08.26, 2026-08-31) et les heures (08:21).
  retire(/\d{1,2}[./]\d{1,2}[./]\d{2,4}/g);
  retire(/\d{4}-\d{2}-\d{2}/g);
  retire(/\d{1,2}:\d{2}(?::\d{2})?/g);
  // Les numéros : carte masquée, IBAN, téléphone, référence QR, montants.
  retire(/[x*]{2,}(?:[x* ]*[x*])? ?\d{2,4}/g);
  // L'IBAN, entier ou en partie : des groupes qui ont tous un chiffre (« CH12
  // 3456 », pas « ROSS » du nom qui suit).
  retire(/\b[a-z]{2}\d{2}(?: ?(?=[a-z]{0,3}\d)[a-z0-9]{4}){1,7}(?: ?\d{1,3})?/g);
  retire(/\+?\d[\d ]{8,}\d/g);
  retire(/\b(?:eur|usd|gbp|chf)\s?\d+(?:[.,]\d{2})?/g);
  retire(/\d+[.,]\d{2}\s?(?:eur|usd|gbp|chf)\b/g);
  retire(BRUIT);
  let texte = lettres.join('').replace(/[*#]/g, ' ');
  // La communication qui suit (« Facture 88412 ») : le commerçant est avant.
  const suite = simple(texte).search(SUITE);
  if (suite > 0 && simple(texte).slice(0, suite).trim()) texte = texte.slice(0, suite);
  texte = texte
    .replace(/\s+/g, ' ')
    .replace(/^[\s/,;:.\-–]+|[\s/,;:\-–]+$/g, '')
    .trim();
  const tete = simple(texte).match(EN_TETE);
  if (tete && tete[0].length < texte.length) texte = texte.slice(tete[0].length);
  // Écrit tout en capitales (PostFinance) : « DENNER SA GENEVE » → « Denner SA Geneve ».
  if (texte && !/\p{Ll}/u.test(texte))
    texte = texte.replace(/\p{L}+/gu, (m) =>
      /^(?:SA|AG|SARL|GMBH|SAGL|BCGE|BCV|UBS|SBB|CFF|FFS|TPG)$/.test(m)
        ? m
        : m[0] + m.slice(1).toLowerCase(),
    );
  return texte;
}

/**
 * Le commerçant d'un libellé de relevé, pour reconnaître le même d'un mois à
 * l'autre : « Achat Mastercard 31.08.2026 08:21 Prestiges Gourmet » →
 * « prestiges gourmet ». La carte, le genre d'ordre, la date et l'heure
 * changent ; le commerçant reste.
 *
 * @param {string} libelle le libellé du mouvement ou de la dépense
 * @returns {string} le commerçant, en minuscules ('' si rien ne reste)
 */
export function commercant(libelle) {
  return simple(commerceDuLibelle(libelle))
    .replace(/[^a-z0-9'&+-]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .slice(0, 3)
    .join(' ');
}

// Des mots qui ne nomment aucun commerçant : la ville, le pays, le terminal.
const GENERIQUES = new Set([
  'sumup',
  'sa',
  'ag',
  'sarl',
  'gmbh',
  'suisse',
  'schweiz',
  'svizzera',
  'switzerland',
  'geneve',
  'genf',
  'lausanne',
  'zurich',
  'bern',
  'berne',
  'basel',
  'bale',
  'lugano',
  'fribourg',
  'neuchatel',
  'sion',
  'carouge',
  'meyrin',
  'lancy',
  'vernier',
  'onex',
  'gare',
  'shop',
]);

/**
 * Le nom du commerce et son adresse, séparés : « Café du Simplon, Rue du
 * Simplon 12, 1006 Lausanne » → nom « Café du Simplon », lieu « Rue du Simplon
 * 12, 1006 Lausanne » ; « Coop Pronto 8001 Zürich » → « Coop Pronto »,
 * « 8001 Zürich ». Sans virgule ni numéro postal, tout est le nom.
 *
 * @param {string} libelle le libellé du mouvement
 * @returns {{nom: string, lieu: string}} le commerce et l'endroit de l'achat
 */
export function nomEtLieu(libelle) {
  const tout = commerceDuLibelle(libelle);
  const virgule = tout.indexOf(', ');
  if (virgule > 0)
    return { nom: tout.slice(0, virgule).trim(), lieu: tout.slice(virgule + 2).trim() };
  // Un numéro postal (4 ou 5 chiffres) suivi d'une ville : l'adresse commence
  // à la rue qui le précède (« Bahnhofstrasse 5 8001 Zürich ») ou à lui.
  const npa = tout.match(
    /\s((?:[\p{L}.'-]+(?:strasse|gasse|weg|platz|rue|route|chemin|avenue|via|calle|rua)\s\d+\w?\s)?\d{4,5}\s\p{L}.*)$/iu,
  );
  if (npa && npa.index > 0) return { nom: tout.slice(0, npa.index).trim(), lieu: npa[1].trim() };
  return { nom: tout, lieu: '' };
}

// Les pays dont la TVA ne se récupère pas en Suisse, par leur code en fin de
// libellé (« Pret A Manger London GB »), et les devises étrangères.
const PAYS_ETRANGERS =
  /\s(?:FR|DE|IT|ES|PT|AT|NL|BE|LU|GB|UK|IE|US|XK|AL|MK|RS|HR|BA|ME|SI|PL|CZ|SK|HU|RO|BG|GR|TR|DK|SE|NO|FI|FRA|DEU|ITA|ESP|PRT|AUT|NLD|BEL|GBR|USA|XKX|ALB|MKD|SRB|HRV|TUR)$/;
const VILLES_OU_PAYS_ETRANGERS =
  /\b(?:france|deutschland|germany|italia|italy|espana|spain|portugal|osterreich|austria|kosovo|kosova|shqiperi|albania|srbija|hrvatska|london|paris|milano|madrid|lisboa|berlin|munchen|wien|prishtine|pristina|tirane|skopje)\b/;

/**
 * Un achat payé à l'étranger : sa TVA est celle d'un autre pays, elle ne se
 * récupère pas dans le décompte suisse (l'AFC n'admet que la TVA suisse).
 *
 * @param {string} libelle le libellé du mouvement
 * @param {string} [devise] la devise du mouvement, si le relevé la donne
 * @returns {boolean} vrai pour un achat hors de Suisse
 */
export function achatALEtranger(libelle, devise) {
  if (devise && !/^(?:CHF)?$/i.test(String(devise).trim())) return true;
  const texte = String(libelle || '').trim();
  if (/\b(?:EUR|USD|GBP)\b/i.test(texte)) return true;
  if (PAYS_ETRANGERS.test(texte)) return true;
  return VILLES_OU_PAYS_ETRANGERS.test(simple(texte));
}

/** Un retrait d'espèces : de l'argent sorti en caisse, pas une dépense. */
export const estUnRetrait = (libelle) =>
  /\b(?:retraits?(?: d.especes)?|bancomat|postomat|geldautomat|bargeldbezug|cash withdrawal|atm withdrawal|prelevement d.especes)\b/.test(
    simple(libelle),
  );

/**
 * La nature d'un mouvement, en un mot : `carte`, `twint`, `recu` (virement
 * reçu), `virement` (ordre de paiement), `lsv` (prélèvement), `permanent`
 * (ordre permanent), `retrait` (espèces), `frais` (frais de la banque) ou
 * `inconnu` (le relevé ne dit pas le sens).
 *
 * @param {object} t mouvement ({sens, label})
 * @returns {string} la nature
 */
export function natureDuMouvement(t) {
  const s = simple(t?.label);
  if (t?.sens !== 'CRDT' && t?.sens !== 'DBIT') return 'inconnu';
  if (estUnRetrait(t.label)) return 'retrait';
  if (/\btwint\b/.test(s)) return 'twint';
  if (/\b(?:lsv\+?|prelevement|recouvrement|lastschrift|debit direct|direct debit)\b/.test(s))
    return 'lsv';
  if (/\b(?:ordre permanent|dauerauftrag|standing order)\b/.test(s)) return 'permanent';
  if (
    t.sens === 'DBIT' &&
    /\b(?:frais|tenue de compte|tenue du compte|cotisation annuelle|interets? debiteurs?|kontofuhrung|spesen|gebuhren)\b/.test(
      s,
    )
  )
    return 'frais';
  if (
    /\b(?:achat|mastercard|maestro|visa|debitkarte|einkauf|kartenzahlung|carte|v pay|tancomat|postfinance card)\b/.test(
      s,
    )
  )
    return 'carte';
  return t.sens === 'CRDT' ? 'recu' : 'virement';
}

/**
 * La catégorie proposée pour une sortie du relevé : celle que le libellé dit
 * (« station-service » → carburant, « TPG » → transports) ; sinon celle d'une
 * dépense déjà rangée chez le même commerçant (le choix du patron se retient)
 * ; sinon rien — le patron choisit, plutôt que tout tomber sous « Matériel ».
 * Un commerçant qui n'a aucun mot à lui (« SumUp », « Genève ») ne se retient
 * pas : tout se rangeait dans la même catégorie. Un retrait d'espèces n'est
 * pas une dépense : rien n'est proposé.
 *
 * @param {string} libelle le libellé du mouvement
 * @param {object[]} [frais] dépenses déjà saisies (`type`, `libelle`)
 * @returns {string} la clé de la catégorie, ou '' à choisir
 */
export function categorieDuMouvement(libelle, frais = []) {
  if (estUnRetrait(libelle)) return '';
  const lue = categorieDuTexte(libelle);
  if (lue && FRAIS_CAT[lue]) return lue;
  const qui = commercant(libelle);
  const aSonNom = qui.split(' ').some((m) => m.length >= 3 && !GENERIQUES.has(m));
  if (aSonNom) {
    const deja = [...(frais || [])]
      .reverse()
      .find((f) => f && FRAIS_CAT[f.type] && commercant(f.libelle) === qui);
    if (deja) return deja.type;
  }
  return '';
}
